import type { JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { PushPermission } from '@/lib/push';

import type { DevicesApi } from './api';

/**
 * What BP-11 §6's notifications screen needs from outside React.
 *
 * The OS half is injected rather than imported, for the reason the
 * licensing screens inject their transport: `@capacitor/push-notifications`
 * has no implementation in jsdom, and a screen that reached for it directly
 * could only be tested by faking a module. Here the four operations are
 * values, so the test writes "the OS said denied" and the screen draws it.
 */
export interface DevicesDeps {
  api: DevicesApi;
  /** The current OS permission, read without asking (R11). */
  permission: () => Promise<PushPermission>;
  /** R11's prompt, then R4's registration. */
  enable: () => Promise<PushPermission>;
  /** R4's logout half: tell the OS to stop delivering. */
  disable: () => Promise<void>;
  /**
   * Open the OS settings page for this app.
   *
   * §6: "denied (OS-level, with 'open settings' via `@capacitor/app`)".
   * Android gives no second prompt once somebody has refused, so the only
   * way back is the settings app — and a screen that just said "denied"
   * with no way forward is the commonest one-star review there is.
   * `undefined` where there is no such page (the browser).
   */
  openSettings?: (() => Promise<void>) | undefined;
}

const DevicesContext = createContext<DevicesDeps | null>(null);

/** Provide the device dependencies to a subtree. */
export function DevicesProvider({
  deps,
  children,
}: {
  deps: DevicesDeps;
  children: ReactNode;
}): JSX.Element {
  return <DevicesContext.Provider value={deps}>{children}</DevicesContext.Provider>;
}

/**
 * The device dependencies.
 *
 * Throws rather than defaulting: a screen rendered outside the provider
 * would otherwise call a transport that is not there, and the failure would
 * read as a network error instead of a wiring mistake.
 */
export function useDevicesDeps(): DevicesDeps {
  const deps = useContext(DevicesContext);
  if (deps === null) {
    throw new Error('useDevicesDeps must be used inside <DevicesProvider>');
  }
  return deps;
}
